// frontend/admin-portal/src/pages/Brands.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Modal,
  Input,
} from "@fiberhome/shared-ui";
import { brandService } from "../services/brandService";

const EMPTY = {
  name: "",
  slug: "",
  country: "",
  website: "",
  logo: "",
  description: "",
  isActive: true,
};

export default function Brands() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await brandService.list({ withCounts: true });
      setItems(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalError("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      slug: item.slug || "",
      country: item.country || "",
      website: item.website || "",
      logo: item.logo || "",
      description: item.description || "",
      isActive: item.isActive !== false,
    });
    setModalError("");
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setModalError("");
    try {
      if (editing?.id) await brandService.update(editing.id, form);
      else await brandService.create(form);
      setModalOpen(false);
      load();
    } catch (err) {
      setModalError(
        err.response?.data?.message || err.message || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await brandService.remove(item.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const filtered = items.filter(
    (b) => !search || b.name.toLowerCase().includes(search.toLowerCase()),
  );

  const columns = [
    {
      key: "name",
      label: "Brand",
      render: (r) => (
        <div className="d-flex align-items-center gap-2">
          {r.logo ? (
            <img
              src={r.logo}
              alt={r.name}
              style={{
                width: 32,
                height: 32,
                objectFit: "contain",
                borderRadius: 6,
                background: "#f8f9fa",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                background: "var(--fh-surface-alt)",
                display: "grid",
                placeItems: "center",
                fontWeight: 600,
                fontSize: 12,
                color: "var(--fh-text-muted)",
              }}
            >
              {r.name.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {r.slug}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "country",
      label: "Country",
      render: (r) => r.country || <span className="text-muted">—</span>,
    },
    {
      key: "website",
      label: "Website",
      render: (r) =>
        r.website ? (
          <a href={r.website} target="_blank" rel="noreferrer noopener">
            {new URL(r.website).hostname}
          </a>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "productCount",
      label: "Products",
      render: (r) => <strong>{r.productCount ?? 0}</strong>,
    },
    {
      key: "isActive",
      label: "Status",
      render: (r) => (
        <Badge variant={r.isActive ? "success" : "default"}>
          {r.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 180,
      render: (r) => (
        <div className="d-flex gap-1">
          <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => remove(r)}
            disabled={r.productCount > 0}
            title={
              r.productCount > 0
                ? "Cannot delete while products use this brand"
                : "Delete"
            }
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Brands"
        subtitle="Manufacturers and vendors of your products"
        breadcrumbs={["Home", "Brands"]}
        actions={
          <Button variant="primary" onClick={openNew}>
            + New Brand
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <input
          className="fh-input"
          placeholder="Search brands..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </Card>

      <Card title={`Brands (${filtered.length})`}>
        <Table
          columns={columns}
          data={filtered}
          loading={loading}
          emptyMessage="No brands"
        />
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit: ${editing.name}` : "New Brand"}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={save} loading={saving}>
              {editing ? "Save" : "Create"}
            </Button>
          </>
        }
      >
        {modalError && (
          <Alert variant="danger" className="mb-3">
            {modalError}
          </Alert>
        )}

        <Input
          label="Name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Slug"
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })}
          hint="Auto-generated from name if empty"
        />
        <Input
          label="Country"
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })}
        />
        <Input
          label="Website"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder="https://..."
        />
        <Input
          label="Logo URL"
          value={form.logo}
          onChange={(e) => setForm({ ...form, logo: e.target.value })}
          placeholder="https://... or /uploads/..."
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />

        <label className="fh-checkbox">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          <span>Active</span>
        </label>
      </Modal>
    </>
  );
}
